import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { ThemeProvider, CssBaseline, createTheme, useMediaQuery } from '@mui/material';
import { deDE } from '@mui/material/locale';
import { useTranslation } from 'react-i18next';
import { lightTheme, darkTheme, stageColors } from './theme';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'crm-theme';

export type StageColorKey = keyof typeof stageColors.light;

interface ThemeModeContextValue {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  stageColors: Record<StageColorKey, string>;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used inside ThemeModeProvider');
  return ctx;
}

function readStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const { i18n } = useTranslation();

  const resolved: 'light' | 'dark' = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // MUI ships its own strings (pagination, autocomplete, table) — composing the locale bundle in
  // is what stops "Rows per page" appearing in the middle of a German screen.
  const theme = useMemo(() => {
    const base = resolved === 'dark' ? darkTheme : lightTheme;
    return i18n.resolvedLanguage === 'de' ? createTheme(base, deDE) : base;
  }, [resolved, i18n.resolvedLanguage]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      mode,
      resolved,
      setMode: setModeState,
      stageColors: resolved === 'dark' ? stageColors.dark : stageColors.light,
    }),
    [mode, resolved],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
